/**
 * useTprmLink - the FOCTTA TPRM shortcut (release 3.1, persona spec 7.3/7.5).
 *
 * GET /v1/tenant/connected-products answers with only what THIS person may
 * open (apps/api-controller/src/modules/connected-products): present when
 * Discovery is paired with Core, Core says TPRM's address, and the caller's
 * role is on the persona table's `disc.tprm_link` list. The web adds no rule
 * of its own, so a link can never show here the API did not confirm, and
 * there is never a dead link. Same cache-across-remounts pattern as
 * use-entitlements.ts.
 */
import { useCallback, useEffect, useState } from 'react';

import { apiFetch, getAuthToken } from './api';

export interface TprmLink {
  baseUrl: string;
  homeUrl: string;
}

interface ConnectedProductsResponse {
  tprm: TprmLink | null;
}

let _cache: TprmLink | null = null;
let _cacheAt = 0;
const CACHE_TTL_MS = 60_000;

/** Test hook: forget the cached answer. */
export function _resetConnectedProductsCacheForTest(): void {
  _cache = null;
  _cacheAt = 0;
}

export function useTprmLink(): TprmLink | null {
  const [tprm, setTprm] = useState<TprmLink | null>(_cache);

  const refresh = useCallback(async () => {
    if (!getAuthToken()) {
      _cache = null;
      setTprm(null);
      return;
    }
    try {
      const res = await apiFetch<ConnectedProductsResponse>('/v1/tenant/connected-products');
      _cache = res.tprm ?? null;
      _cacheAt = Date.now();
      setTprm(_cache);
    } catch {
      // Fail closed (release 3.1 D6): never show a link the API did not
      // confirm just because the read failed.
      _cache = null;
      setTprm(null);
    }
  }, []);

  useEffect(() => {
    if (_cacheAt && Date.now() - _cacheAt < CACHE_TTL_MS) {
      setTprm(_cache);
      return;
    }
    void refresh();
  }, [refresh]);

  return tprm;
}
